import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { SeoHead } from "@/components/seo/SeoHead";

export interface LegalSection {
  /** i18n key suffix appended to `${namespace}.sections.` */
  id: string;
  /** Optional rendered children below the title (e.g. tables, lists). */
  children?: React.ReactNode;
}

interface LegalPageProps {
  /** i18n namespace, e.g. `legal.privacy`, `legal.legalNotice`. */
  namespace: string;
  /** Ordered list of sections to render. */
  sections: LegalSection[];
  /** ISO date string of the document's `lastUpdated` field. */
  lastUpdated: string;
  /** DPO / contact mailto. */
  contactEmail?: string;
}

/**
 * Shared chrome for /politique-rgpd, /mentions-legales, /cgv and /trust.
 *
 * Reads its title, intro and section bodies from i18next so FR and EN versions
 * stay in lockstep without page-level duplication. Pages drop in optional
 * children (tables, contact blocks) per section.
 */
export default function LegalPage({ namespace, sections, lastUpdated, contactEmail = "dpo@delapresse.fr" }: LegalPageProps) {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();

  const dateFormatter = new Intl.DateTimeFormat(i18n.language === "fr" ? "fr-FR" : "en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={t(`${namespace}.title`)}
        description={t(`${namespace}.intro`, { defaultValue: t(`${namespace}.title`) })}
        path={pathname}
        locale={i18n.language === "fr" ? "fr" : "en"}
      />
      <motion.article
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mx-auto max-w-3xl px-6 py-16"
      >
        <header className="mb-10">
          <p className="mb-3 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
            {t("legal.common.label")}
          </p>
          <h1 className="font-serif text-4xl font-bold leading-tight text-foreground md:text-5xl">
            {t(`${namespace}.title`)}
          </h1>
          <p className="mt-4 text-sm text-muted-foreground">
            {t("legal.common.lastUpdated", { date: dateFormatter.format(new Date(lastUpdated)) })}
          </p>
          {t(`${namespace}.intro`, { defaultValue: "" }) && (
            <p className="mt-6 text-base leading-7 text-foreground/90">{t(`${namespace}.intro`)}</p>
          )}
        </header>

        {sections.length > 0 && (
          <nav className="mb-10 rounded-lg border border-border bg-secondary/30 p-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("legal.common.toc")}
            </p>
            <ol className="space-y-1 text-sm">
              {sections.map((section, idx) => (
                <li key={section.id}>
                  <a href={`#${section.id}`} className="text-foreground hover:text-primary hover:underline">
                    {idx + 1}. {t(`${namespace}.sections.${section.id}.title`)}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <div className="space-y-10">
          {sections.map((section, idx) => (
            <section key={section.id} id={section.id} className="scroll-mt-20">
              <h2 className="font-serif text-2xl font-semibold text-foreground">
                {idx + 1}. {t(`${namespace}.sections.${section.id}.title`)}
              </h2>
              <div className="prose prose-sm mt-3 max-w-none whitespace-pre-line text-foreground/90 prose-headings:font-serif prose-p:leading-7 prose-a:text-primary">
                {t(`${namespace}.sections.${section.id}.body`)}
              </div>
              {section.children && <div className="mt-4">{section.children}</div>}
            </section>
          ))}
        </div>

        <footer className="mt-16 border-t border-border pt-8 text-sm text-muted-foreground">
          <p>
            {t("legal.common.contact")}{" "}
            <a href={`mailto:${contactEmail}`} className="text-primary hover:underline">
              {contactEmail}
            </a>
          </p>
          <p className="mt-3">
            <Link to="/" className="text-primary hover:underline">
              {t("legal.common.backHome")}
            </Link>
          </p>
        </footer>
      </motion.article>
    </div>
  );
}
